import { useEffect, useMemo, useState } from "react";
import { Typography, TextField } from "@material-ui/core";
import SmarthopConfirmDialog from "@smarthop/form/SmarthopConfirmDialog";

const PHONE_REGEX = /^[0-9\s()+\-\.#*,extx]+$/i;

const sanitizePhone = (value) => value?.trim() ?? "";

const validateContact = (value) => {
	if (!value || value.trim() === "") {
		return "Driver phone number is required";
	}

	if (!PHONE_REGEX.test(value)) {
		return "Enter a valid phone number";
	}

	return "";
};

function TripCheckCallConfirmModal({ open, trip, initialPhone, onClose, onConfirm, processing }) {
	const [contact, setContact] = useState("");
	const [error, setError] = useState("");

	useEffect(() => {
		if (open) {
			const sanitized = sanitizePhone(initialPhone);
			setContact(sanitized);
			setError(validateContact(sanitized));
		} else {
			setContact("");
			setError("");
		}
		// validateContact reference is stable because it's defined outside of the component scope.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, initialPhone]);

	const driverName = trip?.driver__view?.label ?? trip?.driver_name;

	const tripSummary = useMemo(() => {
		if (!trip) return null;
		const loadId = trip?.load_id ?? trip?.data?.load_id;
		const origin = trip?.origin ?? trip?.data?.origin;
		const destination = trip?.destination ?? trip?.data?.destination;

		return {
			loadId,
			origin,
			destination,
		};
	}, [trip]);

	const handleContactChange = (event) => {
		const newValue = event.target.value;
		setContact(newValue);
		setError(validateContact(newValue));
	};

	const handleConfirm = () => {
		const validationError = validateContact(contact);
		if (validationError) {
			setError(validationError);
			return;
		}
		onConfirm?.(sanitizePhone(contact));
	};

	return (
		<SmarthopConfirmDialog
			open={!!open}
			title="Initiate Driver Check Call"
			message="Confirm the phone number the agent will dial for this driver."
			mode="NORMAL"
			handleClose={onClose}
			handleAccept={handleConfirm}
			acceptMsg={processing ? "Calling..." : "Call Driver"}
			disableAccept={processing || !!error || !contact?.trim()}
		>
			<div className="flex flex-col gap-12 pt-8">
				{tripSummary && (
					<div className="flex flex-col gap-4 pb-8 border-b-1">
						<Typography className="text-12 text-gray-600">
							<strong>Driver:</strong> {driverName || "Unassigned"}
						</Typography>
						{tripSummary.loadId && (
							<Typography className="text-12 text-gray-600">
								<strong>Load:</strong> {tripSummary.loadId}
							</Typography>
						)}
						{tripSummary.origin && tripSummary.destination && (
							<Typography className="text-12 text-gray-600">
								<strong>Route:</strong> {tripSummary.origin} → {tripSummary.destination}
							</Typography>
						)}
					</div>
				)}
				<div className="flex flex-col gap-4">
					<Typography className="text-13 font-semibold">Driver Phone Number</Typography>
					<TextField
						fullWidth
						autoFocus
						variant="outlined"
						size="small"
						value={contact}
						onChange={handleContactChange}
						placeholder="(123) 456-7890"
						error={!!error}
						helperText={error}
						InputProps={{ className: "text-14" }}
						disabled={processing}
					/>
					<Typography className="text-11 text-gray-600 italic">
						We will send this phone number to the check-call workflow so the agent knows who to call.
					</Typography>
				</div>
			</div>
		</SmarthopConfirmDialog>
	);
}

export default TripCheckCallConfirmModal;
